import { FC, useEffect, useState } from "react";
import { URL_DOWNLOAD_VIA_GED } from "customGlobal";
import { fetchDocumentData } from "api";
import { getUIContext } from "api/common";
import auth from "auth";
import { Document } from "composants/satellite/TabDocument";
import { getSocieteEnCours, SOCIETE_EN_COURS_CHANGE_EVENT } from "./societeEnCours";

const LOGO_MIME_TYPES = ["image/png", "image/jpeg"];

// Premier document lié image (png/jpg), en privilégiant les documents applicables
function findLogoDocument(documents: Document[]) {
  const images = documents.filter((d) => LOGO_MIME_TYPES.includes(d.mimeType?.toLowerCase()));
  return images.find((d) => d.applicable) ?? images[0];
}

// <img> ne peut pas envoyer de header Authorization : le token passe en query param
function buildLogoUrl(documentId: string, token: string) {
  const params = getUIContext();
  params.append("access_token", token);
  return `${URL_DOWNLOAD_VIA_GED()}/document/${encodeURIComponent(documentId)}?${params}`;
}

const Monogramme: FC<{ code: string }> = ({ code }) => (
  <div
    title={code}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 8,
      flex: "none",
      borderRadius: "var(--system-radius, 8px)",
      background: "var(--system-hover, #f2f6fb)",
      color: "var(--system-blue, #1268c3)",
      fontFamily: "var(--system-font-mono, monospace)",
      fontWeight: 800,
      fontSize: 12,
    }}
  >
    {code.toUpperCase()}
  </div>
);

const SocieteLogo: FC<{ code: string }> = ({ code }) => {
  // undefined = recherche en cours, null = pas de logo (ou erreur) → monogramme
  const [src, setSrc] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    // On récupère l'id de l'image en GED via le code société qui correspond au nom de l'image
    fetchDocumentData("societe", code, 0, 1)
      .then((response) => {
        const logo = findLogoDocument(response.data.data);
        if (!cancelled) setSrc(logo && auth.token ? buildLogoUrl(logo.id, auth.token) : null);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  if (!src) return <Monogramme code={code} />;

  // src figé au montage : un rafraîchissement ultérieur du token ne recharge pas l'image
  return (
    <img
      src={src}
      alt={code}
      title={code}
      style={{ height: 28, maxWidth: 120, objectFit: "contain", flex: "none" }}
      onError={() => setSrc(null)}
    />
  );
};

// Logo de la société courante : premier document image (png/jpg) lié à la société dans la GED,
// sinon monogramme
const EntityLogoTopBar: FC = () => {
  const [code, setCode] = useState(getSocieteEnCours);

  useEffect(() => {
    const handler = () => setCode(getSocieteEnCours());
    window.addEventListener(SOCIETE_EN_COURS_CHANGE_EVENT, handler);
    return () => window.removeEventListener(SOCIETE_EN_COURS_CHANGE_EVENT, handler);
  }, []);

  if (!code) {
    return null;
  }

  // key={code} : un changement de société remonte le composant (recherche relancée)
  return <SocieteLogo key={code} code={code} />;
};

export default EntityLogoTopBar;
